import { useEffect } from 'react';

import { HeadsetIcon } from '@hugeicons/core-free-icons';
import { useToast } from 'hooks/toast';
import { useForm } from 'react-hook-form';

import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import { Button, Input, InputGroup, Select } from 'components/FormComponents';

import { useGetAllCompaniesListQuery } from 'api/company-api';
import { useCreateSupportAccessRequestMutation } from 'api/support-access-api';

const DURATION_OPTIONS = [24, 1, 2, 4, 8, 48, 72];

const formatDuration = hours => {
  if (hours < 24) return hours + ' ' + (hours === 1 ? 'hour' : 'hours');
  const days = hours / 24;
  return hours + ' hours (' + days + (days === 1 ? ' day)' : ' days)');
};

export const NewSupportAccessModal = ({ visible, onClose, onSubmit, initialValues }) => {
  const {
    register,
    watch,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const { data: companiesData } = useGetAllCompaniesListQuery();
  const [createRequest, { isLoading }] = useCreateSupportAccessRequestMutation();
  const showToast = useToast();

  useEffect(() => {
    if (!visible) return;

    reset({
      company_id: initialValues?.company_id ?? '',
      reason: initialValues?.reason ?? '',
      ticket_number: initialValues?.ticket_number ?? '',
      duration_hours: initialValues?.duration_hours ?? '',
    });
  }, [visible]);

  const companies = companiesData?.result ?? [];

  const handleFormSubmit = async data => {
    try {
      const request = await createRequest(data).unwrap();
      onSubmit(request);
    } catch (error) {
      const message = error?.data?.error?.log || 'Something went wrong. Please try again.';
      showToast('Error', message, 'error');
    }
  };

  return (
    <AnimatedModal
      visible={visible}
      onClose={onClose}
      icon={HeadsetIcon}
      oldIconLibrary={false}
      title="New Support Access Request"
      description="Fill out the form below to request access to a client's account"
      small>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <InputGroup title="Company *">
          <Select
            name="company_id"
            register={register}
            value={watch('company_id')}
            validators={{ required: true }}
            error={errors?.company_id?.type}
            useDefault>
            {companies.map(company => (
              <Select.Item key={company.id} value={company.id}>
                {company.name}
              </Select.Item>
            ))}
          </Select>
        </InputGroup>

        <InputGroup title="Reason *">
          <Input
            register={register}
            name="reason"
            placeholder="Describe the reason for accessing this account"
            validators={{ required: true }}
            error={errors?.reason?.type}
          />
        </InputGroup>

        <InputGroup title="Ticket Number">
          <Input register={register} name="ticket_number" placeholder="e.g. CMP-1234" />
        </InputGroup>

        <InputGroup title="Duration *">
          <Select
            name="duration_hours"
            register={register}
            value={watch('duration_hours')}
            validators={{ required: true }}
            error={errors?.duration_hours?.type}
            useDefault>
            {DURATION_OPTIONS.map(hours => (
              <Select.Item key={hours} value={hours}>
                {formatDuration(hours)}
              </Select.Item>
            ))}
          </Select>
        </InputGroup>

        <div className="flex justify-end gap-2 mt-2">
          <Button type="secondary" onClick={onClose}>
            Close
          </Button>
          <Button type="primary" loading={isLoading} submit>
            Request Access
          </Button>
        </div>
      </form>
    </AnimatedModal>
  );
};
